'use client'

import {
    HealthMapAnswer,
    HealthMapAnswerInput,
    HealthMapCategory,
    HealthMapQuestion,
} from '@/lib/api/health-map/health-map-api'
import { useMemo } from 'react'
import { MdCheckCircle, MdInfoOutline } from 'react-icons/md'

/** Estado de preenchimento em memória, por pergunta. */
export interface HealthMapDraft {
    [questionId: number]: {
        booleanValue?: boolean | null
        textValue?: string
        selectedOptions?: string[]
    }
}

/** Converte as respostas já gravadas no formato editável da tela. */
export function draftFromAnswers(answers: HealthMapAnswer[]): HealthMapDraft {
    const draft: HealthMapDraft = {}
    for (const answer of answers) {
        draft[answer.questionId] = {
            booleanValue: answer.booleanValue ?? null,
            textValue: answer.textValue ?? '',
            selectedOptions: answer.selectedOptions ?? [],
        }
    }
    return draft
}

/** Converte o estado da tela no payload de salvamento. */
export function draftToAnswers(draft: HealthMapDraft): HealthMapAnswerInput[] {
    return Object.entries(draft).map(([questionId, value]) => ({
        questionId: Number(questionId),
        booleanValue: value.booleanValue ?? null,
        textValue: value.textValue ?? null,
        selectedOptions: value.selectedOptions ?? [],
    }))
}

/** Uma pergunta é considerada respondida quando tem QUALQUER valor preenchido. */
export function isAnswered(
    draft: HealthMapDraft,
    question: HealthMapQuestion
): boolean {
    const value = draft[question.id]
    if (!value) return false
    return (
        value.booleanValue != null ||
        !!value.textValue?.trim() ||
        (value.selectedOptions?.length ?? 0) > 0
    )
}

export function countAnswered(
    categories: HealthMapCategory[],
    draft: HealthMapDraft
): { answered: number; total: number } {
    let answered = 0
    let total = 0
    for (const category of categories) {
        for (const theme of category.themes) {
            for (const question of theme.questions) {
                total += 1
                if (isAnswered(draft, question)) answered += 1
            }
        }
    }
    return { answered, total }
}

interface QuestionFieldProps {
    question: HealthMapQuestion
    value: HealthMapDraft[number] | undefined
    onChange: (value: HealthMapDraft[number]) => void
    disabled?: boolean
}

/**
 * Um campo por tipo de resposta.
 *
 * Sim/Não é um par de botões, e não um checkbox: checkbox não distingue "não" de
 * "ainda não respondi", e num questionário clínico essa diferença importa — negar
 * uma dor no peito não é o mesmo que pular a pergunta.
 */
function QuestionField({
    question,
    value,
    onChange,
    disabled,
}: QuestionFieldProps) {
    const options = question.options ?? []

    if (question.type === 'YES_NO') {
        const current = value?.booleanValue ?? null
        return (
            <div className="flex items-center gap-2">
                {[
                    { label: 'Sim', flag: true },
                    { label: 'Não', flag: false },
                ].map(option => (
                    <button
                        key={option.label}
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                            onChange({
                                ...value,
                                // Clicar de novo LIMPA: é como o operador desfaz uma
                                // marcação errada sem recarregar a tela.
                                booleanValue:
                                    current === option.flag ? null : option.flag,
                            })
                        }
                        className={`btn btn-sm ${
                            current === option.flag
                                ? 'btn-secondary'
                                : 'btn-outline'
                        }`}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        )
    }

    if (question.type === 'TEXT') {
        return (
            <textarea
                className="textarea textarea-bordered w-full leading-relaxed"
                rows={3}
                disabled={disabled}
                placeholder="Escreva aqui..."
                value={value?.textValue ?? ''}
                onChange={event =>
                    onChange({ ...value, textValue: event.target.value })
                }
            />
        )
    }

    if (question.type === 'SINGLE_CHOICE') {
        const selected = value?.selectedOptions?.[0] ?? null
        return (
            <div className="flex flex-col gap-2">
                {options.map(option => (
                    <label
                        key={option}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                            selected === option
                                ? 'border-secondary bg-secondary/10'
                                : 'border-base-300/60 hover:bg-base-200/40'
                        }`}
                    >
                        <input
                            type="radio"
                            className="radio radio-sm radio-secondary"
                            disabled={disabled}
                            checked={selected === option}
                            onChange={() =>
                                onChange({
                                    ...value,
                                    selectedOptions: [option],
                                })
                            }
                        />
                        <span className="text-sm">{option}</span>
                    </label>
                ))}
            </div>
        )
    }

    const selectedOptions = value?.selectedOptions ?? []
    return (
        <div className="flex flex-col gap-2">
            {options.map(option => {
                const checked = selectedOptions.includes(option)
                return (
                    <label
                        key={option}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                            checked
                                ? 'border-secondary bg-secondary/10'
                                : 'border-base-300/60 hover:bg-base-200/40'
                        }`}
                    >
                        <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-secondary"
                            disabled={disabled}
                            checked={checked}
                            onChange={() =>
                                onChange({
                                    ...value,
                                    selectedOptions: checked
                                        ? selectedOptions.filter(
                                              item => item !== option
                                          )
                                        : [...selectedOptions, option],
                                })
                            }
                        />
                        <span className="text-sm">{option}</span>
                    </label>
                )
            })}
        </div>
    )
}

interface Props {
    /** Categoria em exibição — a navegação entre elas fica com a tela. */
    category: HealthMapCategory
    draft: HealthMapDraft
    onChange: (draft: HealthMapDraft) => void
    disabled?: boolean
}

/**
 * Perguntas de UMA categoria, agrupadas por tema.
 *
 * Compartilhado entre o prontuário e o link público de propósito: são as mesmas
 * perguntas e as mesmas regras de preenchimento, e duas cópias divergiriam na
 * primeira mudança — com o paciente respondendo uma coisa e o médico lendo outra.
 */
export default function HealthMapForm({
    category,
    draft,
    onChange,
    disabled = false,
}: Props) {
    const themes = useMemo(
        () => category.themes.filter(theme => theme.questions.length > 0),
        [category]
    )

    if (themes.length === 0) {
        return (
            <div className="rounded-xl border border-base-300/50 bg-base-100 p-8 text-center">
                <MdInfoOutline className="mx-auto mb-3 text-4xl text-base-content/20" />
                <p className="text-base-content/60">
                    Esta categoria ainda não tem perguntas.
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {themes.map(theme => (
                <section
                    key={theme.id}
                    className="rounded-2xl border border-base-300 bg-base-100"
                >
                    <header className="rounded-t-2xl border-b border-base-300 bg-base-200/40 px-5 py-4">
                        <h3 className="text-sm font-semibold text-base-content">
                            {theme.name}
                        </h3>
                        {theme.description && (
                            <p className="text-xs text-base-content/50">
                                {theme.description}
                            </p>
                        )}
                    </header>

                    <div className="flex flex-col gap-6 p-5">
                        {theme.questions.map(question => (
                            <div
                                key={question.id}
                                className="flex flex-col gap-2"
                            >
                                <div className="flex items-start gap-2">
                                    <p className="flex-1 text-sm font-medium text-base-content">
                                        {question.title}
                                        {question.required && (
                                            <span className="ml-1 text-error">
                                                *
                                            </span>
                                        )}
                                    </p>
                                    {isAnswered(draft, question) && (
                                        <MdCheckCircle
                                            className="mt-0.5 flex-shrink-0 text-base text-success"
                                            title="Respondida"
                                        />
                                    )}
                                </div>
                                {question.helpText && (
                                    <p className="text-xs text-base-content/50">
                                        {question.helpText}
                                    </p>
                                )}
                                <QuestionField
                                    question={question}
                                    value={draft[question.id]}
                                    disabled={disabled}
                                    onChange={value =>
                                        onChange({
                                            ...draft,
                                            [question.id]: value,
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}
